import type {
  GetOffersResponse_Category,
  Offer,
  Offer_Recommended,
} from '@jotta/grpc-connect-openapi/esm/openapi/offer/v1/offer_pb'
import {
  Offer_ProductFeature_Type,
  Offer_ProductScope,
} from '@jotta/grpc-connect-openapi/esm/openapi/offer/v1/offer_pb'
import { useGetOffers } from './useGetOffers'
import { formatPriceConnectWithInterval } from '@jotta/utils/formatPrice'
import { useLocale } from '@jotta/i18n'
import { BrandIcon } from '@jotta/ui/BrandIcon'
import { Divider } from '@jotta/ui/Divider'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { filesize } from '@jotta/utils/filesize'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePaymentInfo } from '../payment/usePaymentInformation'
import {
  TabContent,
  TabContext,
  UpgradeTabSwitch,
  useTabContext,
} from './UpgradeTab'
import { cn } from '@jotta/utils/css'
import { SupportChannels } from '@jotta/ui/SupportChannels'
import type { UserSeats } from '@jotta/grpc-connect-openapi/esm/openapi/userseats_pb'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { plural, t, Trans } from '@lingui/macro'
import { IntervalSwitch } from './IntervalSwitch'
import { SubscriptionInterval } from '@jotta/grpc-connect-openapi/payment'
import { RecurringSubscriptionInterval } from '@jotta/grpc-connect-openapi/payment'
import { SubscriptionType } from '@jotta/grpc-connect-openapi/customer'
import SvgArrowRight from '@jotta/ui/SvgArrowRight'
import SvgBoldPlus from '@jotta/ui/SvgBoldPlus'
import SvgBoldCheck from '@jotta/ui/SvgBoldCheck'
import SvgUser from '@jotta/ui/SvgUser'
import SvgFillUser from '@jotta/ui/SvgFillUser'
import SvgBoldStar from '@jotta/ui/SvgBoldStar'
import { ProductNotFound } from './ProductNotFound'
import Spinner from '@jotta/ui/Spinner'

function formatUserCount(currentUsers?: UserSeats) {
  const type = currentUsers?.type
  switch (type?.case) {
    case 'num':
      return type.value
    case 'unlimited':
      return '∞'
    case undefined:
      return undefined
    default:
      return exhaustiveGuard(type)
  }
}

function OfferAlert({ offer }: { offer: Offer }) {
  const { data: customer } = useGetCustomer()
  const status = offer.status
  if (!status.case) {
    return null
  }

  switch (status.case) {
    case 'storageExceeded':
      return (
        <div className="body-md-ln bg-upgrade-background p-4 text-grey4">
          <Trans>You store too much for this plan</Trans>
        </div>
      )

    case 'usersExceeded':
      const allowedUsers = status.value.allowedUsers
      const removeUserLink = customer?.family
        ? '/web/family'
        : customer?.business
          ? '/biz/users'
          : undefined
      const userCountOrUnlimited = formatUserCount(allowedUsers)
      return (
        <div className="body-md-ln bg-upgrade-background p-4 text-grey4">
          <p>
            {plural(userCountOrUnlimited ?? 0, {
              one: 'This plan only allows 1 user.',
              other: `This plan only allows ${userCountOrUnlimited} users.`,
            })}
          </p>
          {removeUserLink && (
            <p>
              <Trans id="Users can be removed via <0>settings</0>">
                Users can be removed via&nbsp;
                <Link to={removeUserLink} className="link">
                  settings
                </Link>
              </Trans>
            </p>
          )}
        </div>
      )

    case 'businessUserNotPermitted':
      const notPermitted = status.value
      const firstAdminEmail = notPermitted.businessAdminEmails[0]
      const otherAdminsCount = notPermitted.businessAdminEmails.length - 1
      return (
        <div className="body-md-ln bg-informative p-4 text-informative">
          {otherAdminsCount === 0
            ? t`Your subscription is administered by ${firstAdminEmail}`
            : plural(otherAdminsCount, {
                one: `Your subscription is administered by ${firstAdminEmail} + 1 other`,
                other: `Your subscription is administered by ${firstAdminEmail} + ${otherAdminsCount} others`,
              })}
        </div>
      )

    case 'currentSubscription':
    case 'recommended':
    case 'invalid':
    case 'valid':
      return null

    default:
      return exhaustiveGuard(status)
  }
}

function RecommendedTag({ title }: { title: string }) {
  return (
    <div className="body-sm-ln absolute left-0 top-0 flex h-8 w-full flex-nowrap items-center gap-2 rounded-t-sm bg-upgrade-recommended px-4 text-white-to-black">
      <BrandIcon icon={SvgBoldStar} className="h-full w-4" />
      {title}
    </div>
  )
}

function CurrentPlanTag() {
  return (
    <div className="body-sm-ln absolute left-0 top-0 flex h-8 w-full flex-nowrap items-center gap-2 rounded-t-lg bg-upgrade-background px-4 text-primary">
      <Trans>Your current plan</Trans>
    </div>
  )
}

function ContactSupport({ offer }: { offer: Offer }) {
  if (
    offer.status.case !== 'currentSubscription' ||
    !offer.status.value.contactSupport
  ) {
    return null
  }

  return (
    <div className="body-md-ln mt-8 flex w-[295px] flex-col justify-between self-start overflow-hidden bg-primary p-10 shadow-[0px_0px_10px_0px_rgba(205,201,223,0.80)]">
      <h3 className="h3">{offer.status.value.contactSupport.titleLocalized}</h3>
      <SupportChannels />
    </div>
  )
}

function UsageBar({
  current,
  max,
  danger = false,
}: {
  current: number
  max: number
  danger?: boolean
}) {
  return (
    <div className="relative flex h-[10px] w-full flex-nowrap overflow-hidden rounded-xl bg-white-to-black shadow-[0px_1px_2px_0px_rgba(205,201,223,0.80)]">
      <div
        className={cn('relative h-[10px] w-full', {
          'bg-danger-fg': danger,
          'bg-primary-fg': !danger,
        })}
        style={{ width: `${(current / max) * 100}%` }}
      />
    </div>
  )
}

function UserIndicator({ current, max }: { current: number; max: number }) {
  if (!max || max < current) {
    return null
  }

  return (
    <div className="flex w-full flex-nowrap gap-1 overflow-hidden">
      {[...Array(current)].map((v, i) => (
        <BrandIcon icon={SvgFillUser} width={16} height={16} key={i} />
      ))}
      {[...Array(max - current)].map((v, i) => (
        <BrandIcon icon={SvgUser} width={16} height={16} key={i + current} />
      ))}
    </div>
  )
}

function formatUsedAndAllowedStorage(
  currentStorage: number,
  allowedStorage?: number,
) {
  const usageFormatted = allowedStorage
    ? `${filesize(currentStorage)} / ${filesize(allowedStorage)}`
    : filesize(currentStorage)

  return t`${usageFormatted} storage used`
}

function CurrentPlanInfo({ offer }: { offer: Offer }) {
  if (offer.status.case !== 'currentSubscription') {
    throw new Error('Invalid offer')
  }

  let infoType: 'usage' | 'bandwidth' | 'business' | 'family' = 'usage'

  const statusValue = offer.status.value
  if (statusValue.bandwidthNotice) {
    infoType = 'bandwidth'
  } else if (offer.productScope === Offer_ProductScope.BUSINESS) {
    infoType = 'business'
  } else if (offer.productScope === Offer_ProductScope.HOME) {
    infoType = 'family'
  }

  const allowedStorage =
    statusValue.currentAllowedStorage?.type.case === 'bytes'
      ? Number(statusValue.currentAllowedStorage.type.value)
      : undefined
  const currentStorage =
    statusValue.currentStorage?.type.case === 'bytes'
      ? Number(statusValue.currentStorage.type.value)
      : 0
  const allowedUsers =
    statusValue.allowedUsers?.type.case === 'num'
      ? statusValue.allowedUsers.type.value
      : undefined
  const currentUsers =
    statusValue.currentUsers?.type.case === 'num'
      ? statusValue.currentUsers.type.value
      : undefined

  let storageRemaining = 1
  if (typeof allowedStorage === 'number' && allowedStorage > 0) {
    storageRemaining = 1 - currentStorage / allowedStorage
  }

  switch (infoType) {
    case 'usage': {
      return (
        <div
          className={`body-sm-sh rounded-b-lg px-8 py-6 ${storageRemaining <= 0.1 ? 'bg-danger' : 'bg-upgrade-background'}`}
        >
          {storageRemaining <= 0 ? (
            <h4 className="h4 mb-3">
              <Trans>All out of space!</Trans>
            </h4>
          ) : storageRemaining <= 0.1 ? (
            <h4 className="h4 mb-3">
              <Trans>Storage almost full</Trans>
            </h4>
          ) : null}
          {typeof allowedStorage === 'number' && (
            <UsageBar
              max={allowedStorage}
              current={currentStorage}
              danger={storageRemaining <= 0.1}
            />
          )}
          <div className="body-sm-ln mt-2">
            {formatUsedAndAllowedStorage(currentStorage, allowedStorage)}
          </div>
        </div>
      )
    }
    case 'bandwidth':
      return (
        <div
          className={`body-sm-ln rounded-b-lg px-8 py-6 ${currentStorage >= 6_000_000_000_000 ? 'bg-danger' : 'bg-upgrade-background'}`}
        >
          <h4 className="h4">
            {formatUsedAndAllowedStorage(currentStorage, allowedStorage)}
          </h4>
          <p className="body-sm-ln mt-2">
            {statusValue.bandwidthNotice!.localizedText}
          </p>
        </div>
      )
    case 'business':
    case 'family': {
      return (
        <div
          className={`body-sm-sh rounded-b-lg px-8 py-6 ${storageRemaining <= 0.1 ? 'bg-danger' : 'bg-upgrade-background'}`}
        >
          {currentUsers && allowedUsers && (
            <UserIndicator current={currentUsers} max={allowedUsers} />
          )}
          {typeof allowedStorage === 'number' && (
            <div className="my-3">
              <UsageBar
                max={allowedStorage}
                current={currentStorage}
                danger={storageRemaining <= 0.1}
              />
            </div>
          )}
          <div className="body-sm-ln mt-2">
            {formatUsedAndAllowedStorage(currentStorage, allowedStorage)}
          </div>
        </div>
      )
    }
  }
}

function OfferCTA({
  offer,
  interval,
  isFirstRecommendation,
}: {
  offer: Offer
  interval: RecurringSubscriptionInterval
  isFirstRecommendation: boolean
}) {
  const { status, productCode, extraStorageAvailable, extraSeatsAvailable } =
    offer
  const extraStorageBytes = usePaymentInfo()?.data?.extraStorageBytes ?? 0n
  const extraStorage = filesize(extraStorageBytes)
  const link = `/web/upgrade/${productCode}?interval=${interval === RecurringSubscriptionInterval.MONTHLY_RECURRING ? 'monthly' : 'yearly'}`

  if (status.case !== 'currentSubscription') {
    return (
      <Link
        to={link}
        className={cn('btn w-full', {
          'btn-primary': isFirstRecommendation,
          'btn-outline': !isFirstRecommendation,
        })}
      >
        <Trans>Buy now</Trans>
      </Link>
    )
  }

  if (extraStorageAvailable) {
    return (
      <>
        {extraStorageBytes > 0n && (
          <p>{t`${extraStorage} extra storage added`}</p>
        )}
        <Link to={link} className="btn btn-outline mt-2 w-full">
          <BrandIcon icon={SvgBoldPlus} />
          <Trans>Add more storage</Trans>
        </Link>
      </>
    )
  }

  if (extraSeatsAvailable) {
    return (
      <Link to={link} className="btn btn-outline mt-2 w-full">
        <BrandIcon icon={SvgBoldPlus} />
        <Trans>Add more users</Trans>
      </Link>
    )
  }
}

export function OfferView({
  offer,
  standout = false,
  interval,
}: {
  offer: Offer
  standout?: boolean
  interval: RecurringSubscriptionInterval
}) {
  const { extraStorageAvailable, extraSeatsAvailable } = offer
  const locale = useLocale()
  const price =
    interval === RecurringSubscriptionInterval.MONTHLY_RECURRING
      ? offer.monthly?.basePrice
      : offer.yearly?.basePrice
  const productFeaturesLocalized = offer.productFeatures
  const users = productFeaturesLocalized.find(
    value => value.type === Offer_ProductFeature_Type.USER_COUNT,
  )?.labelLocalized
  const features = productFeaturesLocalized.filter(
    feature =>
      feature.type !== Offer_ProductFeature_Type.DISCLAIMER &&
      feature.type !== Offer_ProductFeature_Type.USER_COUNT,
  )
  const notice = productFeaturesLocalized.filter(
    feature => feature.type === Offer_ProductFeature_Type.DISCLAIMER,
  )
  const isCurrent = offer.status.case === 'currentSubscription'
  const isCurrentAndBusiness =
    isCurrent && offer?.productScope === Offer_ProductScope.BUSINESS
  const isValid =
    offer.status.case === 'valid' ||
    offer.status.case === 'recommended' ||
    (isCurrent && (extraStorageAvailable || extraSeatsAvailable))
  const faded = !isCurrent && !isValid
  const highlighted = !faded && Boolean((price?.amount || 0) > 0 || isCurrent)
  const isFirstRecommendation =
    offer.status.case === 'recommended' && !offer.status.value.priorityIdx
  const recommendedTitle =
    offer.status.case === 'recommended' ? offer.status.value.titleLocalized : ''

  if (offer.status.case === 'invalid') {
    return null
  }

  const showProductHighlight = !isFirstRecommendation && !isCurrent

  return (
    <div
      className={cn(
        'relative flex min-h-[516px] w-[295px] flex-col justify-between rounded-lg bg-primary shadow-[0px_0px_10px_0px_rgba(205,201,223,0.80)]',
        {
          'border-2 border-upgrade-recommended': isFirstRecommendation,
          'min-h-[630px]': isCurrentAndBusiness,
          'min-h-[564px] shadow-[0px_4px_20px_0px_rgba(205,201,223,0.80)]':
            standout,
          'bg-grey1': faded,
        },
      )}
    >
      {isCurrent ? (
        <CurrentPlanTag />
      ) : isFirstRecommendation ? (
        <RecommendedTag title={recommendedTitle} />
      ) : null}

      <div
        className={cn('flex-auto px-6 pt-8 sm:px-8', { 'text-grey4': faded })}
      >
        <p
          className={`body-md-ln ${highlighted ? 'text-upgrade-text-highlight' : ''}`}
        >
          {(showProductHighlight && offer.productHighlightLocalized) ||
            '\u00A0'}
        </p>
        <h3 className="body-2x-ln mt-1">{`${offer.productNameLocalized}${notice.length ? '*' : ''}`}</h3>
        <p className="body-md-ln">
          {price && formatPriceConnectWithInterval(interval, price, locale)}
        </p>
        <Divider className="my-6" />
        <p className="body-sm-ln mb-3">{users}</p>
        {features.map((feature, i) => (
          <div className="flex flex-nowrap items-baseline gap-2" key={i}>
            <p className="relative top-[2px]">
              <BrandIcon icon={SvgBoldCheck} />
            </p>
            <p className="body-sm-ln" key={i}>
              {feature.labelLocalized}
            </p>
          </div>
        ))}
        {notice.length > 0 && (
          <div className="body-sm-ln mt-3">
            {notice.map((note, i) => (
              <p key={i}>* {note.labelLocalized}</p>
            ))}
          </div>
        )}
      </div>
      {isValid && (
        <div className="body-sm-ln mt-2 px-8 pb-8">
          <OfferCTA
            offer={offer}
            interval={interval}
            isFirstRecommendation={isFirstRecommendation}
          />
        </div>
      )}
      {isCurrent && <CurrentPlanInfo offer={offer} />}
      <OfferAlert offer={offer} />
    </div>
  )
}

function OfferCategoryView({
  category,
  interval,
}: {
  category: GetOffersResponse_Category
  interval: RecurringSubscriptionInterval
}) {
  return (
    <div className="flex flex-wrap items-start justify-center gap-4 pb-6 pt-8">
      {category.offers.map(value => (
        <OfferView
          key={value.productCode}
          offer={value}
          interval={interval}
        ></OfferView>
      ))}
    </div>
  )
}

function RecommendedPlans({
  offers,
  current,
  onNavigate,
  interval,
  setInterval,
  showIntervalSwitch,
}: {
  offers: Offer[]
  current?: Offer
  onNavigate: () => void
  interval: RecurringSubscriptionInterval
  setInterval: (interval: RecurringSubscriptionInterval) => void
  showIntervalSwitch: boolean
}) {
  const currentIsBusiness =
    current?.productScope === Offer_ProductScope.BUSINESS
  return (
    <>
      <h1 className="title-md self-center">
        <Trans>Need a change?</Trans>
      </h1>
      <button
        className="label-sm flex h-12 flex-nowrap items-center gap-2 self-center text-nowrap px-4"
        onClick={() => onNavigate()}
      >
        <Trans>See all plans</Trans>
        <BrandIcon icon={SvgArrowRight} width={16} height={16} />
      </button>
      <div className="flex-1"></div>
      <div className="mx-auto w-full max-w-screen-xl">
        {showIntervalSwitch && (
          <div className="flex items-center justify-center pt-6">
            <IntervalSwitch
              interval={interval}
              setInterval={setInterval}
              className="self-center"
            />
          </div>
        )}
        <div
          className={cn(
            'flex flex-wrap items-center justify-center gap-6 pb-6 pt-12',
            {
              'items-start': currentIsBusiness,
            },
          )}
        >
          {current && <OfferView offer={current} interval={interval} />}
          {offers.map(offer => (
            <OfferView
              offer={offer}
              key={offer.productCode}
              standout={
                !currentIsBusiness &&
                offer.status.case === 'recommended' &&
                !offer.status.value.priorityIdx
              }
              interval={interval}
            />
          ))}
          {current && <ContactSupport offer={current} />}
        </div>
      </div>
    </>
  )
}

function AllPlans({
  categories,
  onNavigate,
  hasRecommended,
  interval,
  setInterval,
  showIntervalSwitch,
}: {
  categories: GetOffersResponse_Category[]
  onNavigate: () => void
  hasRecommended: boolean
  interval: RecurringSubscriptionInterval
  setInterval: (interval: RecurringSubscriptionInterval) => void
  showIntervalSwitch: boolean
}) {
  return (
    <>
      <h1 className="title-md self-center">
        <Trans>Subscription plans</Trans>
      </h1>
      {hasRecommended && (
        <button
          className="label-sm flex h-12 flex-nowrap items-center gap-2 self-center text-nowrap px-4"
          onClick={() => onNavigate()}
        >
          <Trans>See recommended plans</Trans>
          <BrandIcon icon={SvgArrowRight} width={16} height={16} />
        </button>
      )}

      <div className="w-full">
        {showIntervalSwitch && (
          <div className="flex items-center justify-center pt-6">
            <IntervalSwitch
              interval={interval}
              setInterval={setInterval}
              className="self-center"
            />
          </div>
        )}
        <UpgradeTabSwitch>
          {categories.map(category => (
            <TabContent
              name={category.nameLocalized}
              key={category.nameLocalized}
            >
              <OfferCategoryView
                category={category}
                interval={interval}
              ></OfferCategoryView>
            </TabContent>
          ))}
        </UpgradeTabSwitch>
      </div>
    </>
  )
}

export const Upgrade: React.FC = () => {
  const currentInterval = usePaymentInfo()?.data?.subscriptionInterval
  const showIntervalSwitch =
    useGetCustomer()?.data?.subscriptionType === SubscriptionType.FREE
  const { data: getOffersResponse, isLoading } = useGetOffers()
  const categories = useMemo(
    () =>
      getOffersResponse?.categories.filter(category =>
        category.offers.some(offer => offer.status.case !== 'invalid'),
      ) || [],
    [getOffersResponse],
  )
  const offers = useMemo(
    () => categories.map(category => category.offers).flat(),
    [categories],
  )

  const ctx = useTabContext()
  const { setSelected: setSelectedTab } = ctx

  const current = useMemo(
    () => offers.find(offer => offer.status.case === 'currentSubscription'),
    [offers],
  )
  const [interval, setInterval] = useState<RecurringSubscriptionInterval>(
    currentInterval === SubscriptionInterval.MONTHLY
      ? RecurringSubscriptionInterval.MONTHLY_RECURRING
      : RecurringSubscriptionInterval.YEARLY_RECURRING,
  )
  const recommended = useMemo(
    () =>
      offers
        .filter(offer => offer.status.case === 'recommended')
        .sort(
          (a, b) =>
            (a.status.value as Offer_Recommended).priorityIdx -
            (b.status.value as Offer_Recommended).priorityIdx,
        ),
    [offers],
  )

  useEffect(() => {
    if (recommended.length) {
      const recommendedCategory = categories.find(category =>
        category.offers.some(
          offer => offer.productCode === recommended[0].productCode,
        ),
      )
      recommendedCategory && setSelectedTab(recommendedCategory.nameLocalized)
    }
  }, [recommended, categories, setSelectedTab])

  const [showRecommended, setShowRecommended] = useState(true)

  useEffect(() => {
    if (!isLoading && !recommended.length) {
      setShowRecommended(false)
    }
  }, [isLoading, recommended])

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!offers.length) {
    return <ProductNotFound />
  }

  return (
    <div className="size-full flex-auto bg-upgrade-background">
      <div className="mx-auto flex max-w-[1381px] flex-col px-12 pb-0 pt-16 sm:px-16">
        <TabContext.Provider value={ctx}>
          {showRecommended ? (
            <RecommendedPlans
              offers={recommended}
              current={current}
              onNavigate={() => setShowRecommended(false)}
              interval={interval}
              setInterval={setInterval}
              showIntervalSwitch={showIntervalSwitch}
            />
          ) : (
            <AllPlans
              categories={categories}
              hasRecommended={recommended.length > 0}
              onNavigate={() => setShowRecommended(true)}
              interval={interval}
              setInterval={setInterval}
              showIntervalSwitch={showIntervalSwitch}
            />
          )}
        </TabContext.Provider>
      </div>
    </div>
  )
}
